import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../App';
import { 
  ArrowLeft, Heart, Bookmark, Share2, Edit, Trash2, Plus, 
  Lock, Globe, Image, Package, X, ExternalLink, MoreHorizontal,
  Droplets, Utensils, Sun, Gem, Trees, Crown, Factory, Flame, Snowflake, Folder
} from 'lucide-react';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CATEGORY_ICONS = {
  bathroom_spa: Droplets,
  modern_kitchen: Utensils,
  mediterranean: Sun,
  minimal_luxury: Gem,
  outdoor_living: Trees,
  classic_elegance: Crown,
  industrial_chic: Factory,
  warm_tones: Flame,
  cool_tones: Snowflake,
  other: Folder
};

function AddImageModal({ isOpen, onClose, onAdd }) {
  const [imageUrl, setImageUrl] = useState('');
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!imageUrl.trim()) {
      toast.error('Inserisci URL immagine');
      return;
    }
    onAdd({ image_url: imageUrl, image_title: title, notes });
    setImageUrl('');
    setTitle('');
    setNotes('');
    onClose();
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-medium">Aggiungi Immagine</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">URL Immagine *</label>
            <input
              type="url"
              value={imageUrl}
              onChange={e => setImageUrl(e.target.value)}
              placeholder="https://..."
              className="w-full border px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Titolo</label>
            <input
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              className="w-full border px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Note</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={2}
              className="w-full border px-3 py-2"
            />
          </div>
          <button type="submit" className="w-full bg-neutral-900 text-white py-2 hover:bg-neutral-800">
            Aggiungi
          </button>
        </form>
      </div>
    </div>
  );
}

export default function CollectionDetailPage() {
  const { collectionId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [collection, setCollection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddImage, setShowAddImage] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  
  const fetchCollection = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/collections/${collectionId}`, { withCredentials: true });
      setCollection(response.data);
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('Collezione non trovata');
        navigate('/collections');
      } else if (error.response?.status === 403) {
        toast.error('Collezione privata');
        navigate('/collections');
      }
    } finally {
      setLoading(false);
    }
  }, [collectionId, navigate]);
  
  useEffect(() => {
    fetchCollection();
  }, [fetchCollection]);
  
  const handleLike = async () => {
    if (!user) {
      toast.error('Accedi per mettere like');
      return;
    }
    try {
      if (collection.is_liked) {
        await axios.delete(`${API}/collections/${collectionId}/like`, { withCredentials: true });
      } else {
        await axios.post(`${API}/collections/${collectionId}/like`, {}, { withCredentials: true });
      }
      fetchCollection();
    } catch (error) {
      toast.error('Errore');
    }
  };
  
  const handleSave = async () => {
    if (!user) {
      toast.error('Accedi per salvare');
      return;
    }
    try {
      if (collection.is_saved) {
        await axios.delete(`${API}/collections/${collectionId}/save`, { withCredentials: true });
        toast.success('Rimosso dai salvati');
      } else {
        await axios.post(`${API}/collections/${collectionId}/save`, {}, { withCredentials: true });
        toast.success('Salvato!');
      }
      fetchCollection();
    } catch (error) {
      toast.error('Errore');
    }
  };
  
  const handleAddImage = async (imageData) => {
    try {
      await axios.post(`${API}/collections/${collectionId}/items`, {
        item_type: 'image',
        ...imageData
      }, { withCredentials: true });
      toast.success('Immagine aggiunta');
      fetchCollection();
    } catch (error) {
      toast.error('Errore');
    }
  };
  
  const handleRemoveItem = async (itemId) => {
    if (!window.confirm('Rimuovere questo elemento?')) return;
    try {
      await axios.delete(`${API}/collections/${collectionId}/items/${itemId}`, { withCredentials: true });
      toast.success('Elemento rimosso');
      fetchCollection();
    } catch (error) {
      toast.error('Errore');
    }
  };
  
  const handleDelete = async () => {
    if (!window.confirm('Eliminare questa collezione?')) return;
    try {
      await axios.delete(`${API}/collections/${collectionId}`, { withCredentials: true });
      toast.success('Collezione eliminata');
      navigate('/collections');
    } catch (error) {
      toast.error('Errore');
    }
  };
  
  const copyShareLink = () => {
    if (collection.share_token) {
      const link = `${window.location.origin}/collections/shared/${collection.share_token}`;
      navigator.clipboard.writeText(link);
      toast.success('Link copiato!');
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-neutral-900 border-t-transparent rounded-full" />
      </div>
    );
  }
  
  if (!collection) return null;
  
  const CategoryIcon = CATEGORY_ICONS[collection.category] || Folder;
  const isOwner = collection.is_owner;
  
  // Separate materials and images
  const materials = collection.items?.filter(i => i.item_type === 'material') || [];
  const images = collection.items?.filter(i => i.item_type === 'image') || [];
  
  return (
    <div className="min-h-screen bg-neutral-50 flex flex-col" data-testid="collection-detail-page">
      <Header />
      
      <div className="flex-1 pt-20">
        {/* Page Header */}
        <div className="bg-white border-b border-neutral-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
          <Link to="/collections" className="inline-flex items-center gap-2 text-neutral-500 hover:text-neutral-900 mb-4">
            <ArrowLeft className="w-4 h-4" />
            Torna alle Collezioni
          </Link>
          
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <CategoryIcon className="w-6 h-6 text-neutral-400" />
                <span className="text-sm text-neutral-500 capitalize">{collection.category?.replace('_', ' ')}</span>
                <span className={`text-xs px-2 py-0.5 ${
                  collection.visibility === 'public' ? 'bg-green-100 text-green-700' :
                  collection.visibility === 'shared' ? 'bg-[var(--gold)]/20 text-[var(--gold)]' :
                  'bg-neutral-100 text-neutral-600'
                }`}>
                  {collection.visibility === 'public' ? 'Pubblica' :
                   collection.visibility === 'shared' ? 'Condivisibile' : 'Privata'}
                </span>
              </div>
              
              <h1 className="text-3xl font-light text-neutral-900 mb-2">{collection.name}</h1>
              
              {collection.description && (
                <p className="text-neutral-600 mb-3">{collection.description}</p>
              )}
              
              <div className="flex items-center gap-4 text-sm text-neutral-500">
                <span>di <strong>{collection.user_name}</strong></span>
                <span>{collection.materials_count} materiali</span>
                <span>{collection.images_count} immagini</span>
              </div>
              
              {collection.tags?.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {collection.tags.map((tag, i) => (
                    <span key={i} className="text-xs bg-neutral-100 text-neutral-600 px-2 py-1">
                      #{tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
            
            {/* Actions */}
            <div className="flex items-center gap-2">
              <button
                onClick={handleLike}
                className={`flex items-center gap-2 px-4 py-2 border ${
                  collection.is_liked 
                    ? 'border-red-200 bg-red-50 text-red-600' 
                    : 'border-neutral-300 hover:bg-neutral-50'
                }`}
              >
                <Heart className={`w-4 h-4 ${collection.is_liked ? 'fill-current' : ''}`} />
                {collection.likes_count || 0}
              </button>
              
              <button
                onClick={handleSave}
                className={`flex items-center gap-2 px-4 py-2 border ${
                  collection.is_saved 
                    ? 'border-[var(--gold)]/30 bg-[var(--gold)]/10 text-[var(--gold)]' 
                    : 'border-neutral-300 hover:bg-neutral-50'
                }`}
              >
                <Bookmark className={`w-4 h-4 ${collection.is_saved ? 'fill-current' : ''}`} />
                Salva
              </button>
              
              {(collection.visibility === 'shared' || collection.visibility === 'public') && collection.share_token && (
                <button
                  onClick={copyShareLink}
                  className="flex items-center gap-2 px-4 py-2 border border-neutral-300 hover:bg-neutral-50"
                >
                  <Share2 className="w-4 h-4" />
                  Condividi
                </button>
              )}
              
              {isOwner && (
                <button
                  onClick={handleDelete}
                  className="flex items-center gap-2 px-4 py-2 border border-red-300 text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Images Section */}
        {(images.length > 0 || isOwner) && (
          <div className="mb-12">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-light flex items-center gap-2">
                <Image className="w-5 h-5" />
                Immagini Ispirazionali ({images.length})
              </h2>
              {isOwner && (
                <button
                  onClick={() => setShowAddImage(true)}
                  className="flex items-center gap-2 text-sm bg-neutral-900 text-white px-3 py-1.5 hover:bg-neutral-800"
                >
                  <Plus className="w-4 h-4" />
                  Aggiungi
                </button>
              )}
            </div>
            
            {images.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {images.map(item => (
                  <div key={item.item_id} className="group relative aspect-square bg-neutral-100 overflow-hidden">
                    <img 
                      src={item.image_url} 
                      alt={item.image_title || 'Inspiration'}
                      className="w-full h-full object-cover"
                    />
                    {isOwner && (
                      <button
                        onClick={() => handleRemoveItem(item.item_id)}
                        className="absolute top-2 right-2 p-1.5 bg-white/90 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-50"
                      >
                        <X className="w-4 h-4 text-red-600" />
                      </button>
                    )}
                    {item.image_title && (
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3">
                        <p className="text-white text-sm">{item.image_title}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 bg-white border border-dashed border-neutral-300">
                <Image className="w-12 h-12 mx-auto text-neutral-300 mb-2" />
                <p className="text-neutral-500 text-sm">Nessuna immagine ancora</p>
              </div>
            )}
          </div>
        )}
        
        {/* Materials Section */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-light flex items-center gap-2">
              <Package className="w-5 h-5" />
              Materiali ({materials.length})
            </h2>
          </div>
          
          {materials.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {materials.map(item => {
                const material = item.material;
                if (!material) return null;
                
                return (
                  <div key={item.item_id} className="group bg-white border border-neutral-200 overflow-hidden">
                    <div className="aspect-square bg-neutral-100 relative overflow-hidden">
                      {material.images?.[0] ? (
                        <img 
                          src={material.images[0]} 
                          alt={material.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Package className="w-12 h-12 text-neutral-300" />
                        </div>
                      )}
                      {isOwner && (
                        <button
                          onClick={() => handleRemoveItem(item.item_id)}
                          className="absolute top-2 right-2 p-1.5 bg-white/90 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-50"
                        >
                          <X className="w-4 h-4 text-red-600" />
                        </button>
                      )}
                    </div>
                    <div className="p-4">
                      <h3 className="font-medium text-neutral-900 mb-1">{material.name}</h3>
                      <p className="text-sm text-neutral-500 mb-2">{material.stone_type}</p>
                      {item.notes && (
                        <p className="text-xs text-neutral-400 italic">{item.notes}</p>
                      )}
                      <Link
                        to={`/materials/${material.material_id}`}
                        className="inline-flex items-center gap-1 text-sm text-neutral-600 hover:text-neutral-900 mt-2"
                      >
                        Vedi dettagli <ExternalLink className="w-3 h-3" />
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12 bg-white border border-dashed border-neutral-300">
              <Package className="w-12 h-12 mx-auto text-neutral-300 mb-2" />
              <p className="text-neutral-500 mb-4">Nessun materiale in questa collezione</p>
              <Link
                to="/materials"
                className="inline-flex items-center gap-2 text-sm bg-neutral-900 text-white px-4 py-2 hover:bg-neutral-800"
              >
                Esplora Materiali
              </Link>
            </div>
          )}
        </div>
      </div>
      </div>
      
      {/* Add Image Modal */}
      <AddImageModal
        isOpen={showAddImage}
        onClose={() => setShowAddImage(false)}
        onAdd={handleAddImage}
      />
      
      <Footer />
    </div>
  );
}
